/**
 * Funding & Growth (TRUS-776) — Organization Settings tab.
 *
 * The org-scoped half of what Program & Provider Import does for the platform:
 * this organization confirms what it delivers and where, the finder matches
 * that against the payer directory, the program catalog and discovered
 * sponsors, and the agent drafts outreach to new funders that a person
 * approves. Attribution closes the loop: one service, every funder that would
 * pay for it, existing and new.
 *
 * Shown only when the org has the `funding_opportunities` capability
 * (OrganizationSettingsPage gates the tab; every API route also returns 403
 * without it). All calls pin X-CBO-ID to `organizationId`, not the viewer's
 * active org.
 */
import React, { useMemo, useState } from 'react';
import { SectionHeader, StatTile, StatTileGrid } from '@/components/weavein';
import { fundingApi } from '@/services/api/fundingOpportunities';
import CapabilitiesPanel from './CapabilitiesPanel';
import SourcesPanel from './SourcesPanel';
import OpportunitiesPanel from './OpportunitiesPanel';
import OutreachPanel from './OutreachPanel';
import AttributionPanel from './AttributionPanel';
import { ErrorNote, useLoad } from './shared';

type SubTab = 'capabilities' | 'sources' | 'opportunities' | 'outreach' | 'attribution';

const SUB_TABS: { id: SubTab; label: string }[] = [
  { id: 'capabilities', label: 'Capabilities' },
  { id: 'sources', label: 'Sources' },
  { id: 'opportunities', label: 'Opportunities' },
  { id: 'outreach', label: 'Outreach' },
  { id: 'attribution', label: 'Attribution' },
];

interface Props {
  organizationId: number;
}

export default function FundingGrowthSettings({ organizationId }: Props) {
  const api = useMemo(() => fundingApi(organizationId), [organizationId]);
  const [tab, setTab] = useState<SubTab>('capabilities');
  const summary = useLoad(() => api.summary(), [api]);
  const refreshSummary = () => void summary.reload();
  const s = summary.data;

  return (
    <div className="space-y-4" data-testid="funding-growth-settings">
      <SectionHeader
        title="Funding & Growth"
        description="Confirm what you deliver and where, find funders you qualify for, and reach them. Agents draft; people approve."
      />

      {summary.error && <ErrorNote message={summary.error} />}
      <StatTileGrid>
        <StatTile label="Capabilities confirmed" value={s ? s.capabilities.confirmed : '—'} caption={s ? `${s.capabilities.proposed} to review` : undefined} />
        <StatTile label="New funders" value={s ? s.opportunities.new : '—'} tone="accent" caption={s ? `${s.opportunities.pursuing} pursuing` : undefined} />
        <StatTile label="Funders you bill" value={s ? s.opportunities.existing : '—'} tone="success" />
        <StatTile label="Outreach" value={s ? s.outreach.sent : '—'} caption={s ? `${s.outreach.draft} drafts waiting` : undefined} />
      </StatTileGrid>

      <div role="tablist" aria-label="Funding & Growth" className="flex flex-wrap gap-1 border-b border-v2border">
        {SUB_TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            type="button"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`-mb-px border-b-2 px-3 py-2 text-[13px] font-medium transition-colors ${
              tab === t.id ? 'border-v2accent text-ink' : 'border-transparent text-ink-500 hover:text-ink'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div role="tabpanel">
        {tab === 'capabilities' && <CapabilitiesPanel api={api} onChanged={refreshSummary} />}
        {tab === 'sources' && <SourcesPanel api={api} />}
        {tab === 'opportunities' && (
          <OpportunitiesPanel api={api} onChanged={refreshSummary} onOpenOutreach={() => setTab('outreach')} />
        )}
        {tab === 'outreach' && <OutreachPanel api={api} onChanged={refreshSummary} />}
        {tab === 'attribution' && <AttributionPanel api={api} />}
      </div>
    </div>
  );
}
